import { type FieldPolicy, type Reference } from "@apollo/client";

interface RemoteAssetsConnection {
    nodes: Reference[];
    cursor: string | null;
}

export const cacheRemoteAssetsConfig: Record<string, FieldPolicy> = {
    remoteAsset: {
        read: (_, { args, toReference }) => {
            const input = args?.["input"] as { id?: string } | undefined;
            if (!input?.id) return;
            return toReference({ __typename: "RemoteAsset", id: input.id });
        },
    },
    remoteAssets: {
        // Cache key is scoped by organizationId and query. Pagination args
        // (cursor, limit) are excluded so all pages share one cache slot.
        // NOTE: query uses `null` for empty search (not `undefined`). Passing
        // `undefined` would produce a different cache key — keep consistent.
        keyArgs: [
            ["input", "organizationId"],
            ["input", "query"],
        ],
        read: (existing: RemoteAssetsConnection | undefined, { canRead }) => {
            if (!existing) return existing;
            // Filter out evicted entities (e.g. after delete) and return a
            // new object so optimism detects the change and broadcasts.
            return {
                ...existing,
                nodes: existing.nodes.filter((ref) => canRead(ref)),
            };
        },
        // Pagination append is handled by updateQuery in useRemoteAssets
        // because type-policy merge alone doesn't trigger useQuery re-renders
        // with cache-first in Apollo 3.9.x (confirmed by local repro).
        //
        // When updateQuery is used, fetchMore writes the already-merged list
        // back to the cache with the ORIGINAL variables (cursor: null), so
        // merge always sees cursor=null from that path and returns incoming
        // (the full accumulated list). This is correct.
        //
        // When cursor IS present (future: if updateQuery is removed after an
        // Apollo upgrade), merge appends pages directly as a fallback.
        merge: (
            existing: RemoteAssetsConnection | undefined,
            incoming: RemoteAssetsConnection,
            { args }: { args: Record<string, Record<string, unknown>> | null },
        ) => {
            const cursor = args?.["input"]?.["cursor"];
            if (!existing || !cursor) return incoming;
            return {
                ...incoming,
                nodes: [...existing.nodes, ...incoming.nodes],
            };
        },
    },
};
